import React from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Skeleton } from 'antd';
import { RiskBadge } from '../../../../RiskBadge';
import { useGetOverViewRiskFactors } from './hooks/useGetOverViewRiskFactors';
import type { ColumnsType } from 'antd/lib/table';
import type { Activity, Review, CategoryResult, TableRowData } from './hooks/useGetOverViewRiskFactors';
import * as S from './styles';

export function OverViewRiskFactors(): JSX.Element {
	const { isLoading, activities } = useGetOverViewRiskFactors();

	function formatVersionLabel(date: string, index: number): string {
		const formattedDate = moment(date).format('DD/MM/YYYY');
		const versionName = index === 0 ? I18n.get('Initial version') : `${I18n.get('Review')} ${index}`;
		return `${versionName} (${formattedDate})`;
	}

	function findCategoryInReview(review: Review, categoryName: string): CategoryResult | undefined {
		return review.category_results.find((category) => category.category.name === categoryName);
	}

	function buildTableData(activity: Activity): TableRowData[] {
		const { reviews } = activity;

		if (!reviews.length) {
			return [];
		}

		const sortedReviews = reviews.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
		const firstReviewCategories = sortedReviews[0].category_results;

		return firstReviewCategories.map((categoryResult, index) => {
			const { category } = categoryResult;
			const rowData: TableRowData = {
				key: `${category.id}-${index}`,
				risk_factor: I18n.get(category.name)
			};

			sortedReviews.forEach((review, reviewIndex: number) => {
				const categoryData = findCategoryInReview(review, category.name);
				rowData[`review_${reviewIndex}`] = categoryData?.risk || '';
			});

			return rowData;
		});
	}

	function buildTableColumns(reviews: Review[]): ColumnsType<TableRowData> {
		const sortedReviews = reviews.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
		return [
			{
				title: I18n.get('Risk Factor / Version'),
				dataIndex: 'risk_factor',
				key: 'risk_factor',
				align: 'center',
				width: '30%'
			},
			...sortedReviews.map((review, index) => ({
				title: formatVersionLabel(review.date, index),
				dataIndex: `review_${index}`,
				key: `review_${index}`,
				align: 'center' as const,
				width: `${70 / sortedReviews.length}%`,
				render: (riskLevel: string) => (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<RiskBadge risk={riskLevel} />
					</div>
				)
			}))
		];
	}

	return (
		<Row gutter={[32, 16]}>
			<Col span={24}>
				<div style={{ marginBottom: '16px' }}>
					<S.TaskTitle>{I18n.get('Overview of risk factors')}</S.TaskTitle>
				</div>
				{activities.map((activity) => (
					<div key={activity.id} style={{ marginBottom: '32px' }}>
						<div style={{ marginBottom: '16px' }} />
						<S.TaskHeader>
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									width: '100%'
								}}
							>
								<span>{activity.name}</span>
							</div>
						</S.TaskHeader>
						{isLoading ? (
							<Skeleton active />
						) : (
							<S.StyledTable
								columns={buildTableColumns(activity.reviews) as ColumnsType<object>}
								dataSource={buildTableData(activity)}
								pagination={false}
								rowKey="key"
							/>
						)}
					</div>
				))}
			</Col>
		</Row>
	);
}
